import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import {
  ClusterTitleWrapper,
  ClusterTitleContainer,
  StyledIndex,
  ClusterTitleTextField,
  CustomIcon,
} from './styled';
import { Block } from 'src/config/api/models/Block';
import { patchCluster, releaseCluster } from 'src/modules/shared/slices/blocks/api/blocks';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useUser } from 'src/modules/shared';
import { IconButton, Tooltip } from '@material-ui/core';
import useKeyBindings from 'src/modules/shared/hooks/useKeyBindings';
import { setBlockToDelete } from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import releaseClusterIcon from 'src/assets/icons/break_cluster.svg';

type ClusterTitleProps = {
  index: number | string;
  cluster: Block;
};

export const ClusterTitle: FC<ClusterTitleProps> = ({ index, cluster }) => {
  const { title, id } = cluster;
  const { isStudent } = useUser();
  const dispatch = useAppDispatch();

  const pageId = useAppSelector((state) => state.trajectories.currentPageId);
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const success = useAppSelector((state) => state.blocks.success);
  const error = useAppSelector((state) => state.blocks.error);
  const loading = useAppSelector((state) => state.blocks.loading);

  const [editTitle, setEditTitle] = useState<boolean>();
  const [newTitle, setNewTitle] = useState<string>(title ? title : '');

  const isHoverEnabled = viewMode === 'edit' && !isStudent;

  const handleEditClick = useCallback(() => {
    if (isHoverEnabled) {
      setEditTitle(true);
    }
  }, [isHoverEnabled]);

  const handleBlur = useCallback(() => {
    if (newTitle !== title) {
      dispatch(
        patchCluster({
          clusterId: id,
          payload: { title: newTitle, pageId },
        })
      );
    } else {
      setEditTitle(false);
    }
  }, [dispatch, id, newTitle, pageId, title]);

  useEffect(() => {
    if (success || error) {
      setEditTitle(false);
      dispatch(setBlockToDelete(undefined));
    }
  }, [dispatch, error, success]);

  useKeyBindings({
    Enter: () => handleBlur(),
  });

  const handleReleaseCluster = useCallback(() => {
    if (!pageId) return;
    dispatch(setBlockToDelete(id));
    dispatch(
      releaseCluster({
        pageId,
        clusterId: id,
      })
    );
  }, [dispatch, id, pageId]);

  return (
    <ClusterTitleWrapper>
      <StyledIndex variant="h2">{`${index} | `}</StyledIndex>
      {!editTitle && (
        <>
          <ClusterTitleContainer
            ishoverenabled={isHoverEnabled}
            variant="h2"
            onClick={() => handleEditClick()}
          >
            {title}
          </ClusterTitleContainer>
          {viewMode === 'edit' && (
            <Tooltip placement="left" title="Blokken losmaken">
              <span>
                <IconButton onClick={() => handleReleaseCluster()}>
                  <CustomIcon src={releaseClusterIcon} alt="Icon to release cluster" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )}
      {editTitle && (
        <ClusterTitleContainer variant="h2">
          <ClusterTitleTextField
            autoFocus
            disabled={loading}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setNewTitle(e.target.value);
            }}
            onBlur={handleBlur}
            value={newTitle}
            variant="outlined"
            placeholder={title === '' ? 'Vul een titel in' : ''}
          >
            <ClusterTitleContainer variant="h2"></ClusterTitleContainer>
          </ClusterTitleTextField>
        </ClusterTitleContainer>
      )}
    </ClusterTitleWrapper>
  );
};
